<script lang="ts">
	import type { OrderProductOptions } from "../../../../core/schema/OrderProductOptions.js";
	import type { ProductForCustomer } from "../../../../core/schema/Product.js";
	import DynamicPicture from "../Picture/DynamicPicture.svelte";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import { formatPrice } from "../../../../core/schema/utils/formatPrice.js";
	import { getPriceForProductForCustomerOfSize } from "../../../../core/schema/utils/getPriceForProductForCustomerOfSize.js";
	import ProductConfiguratorOption from "./ProductConfiguratorOption.svelte";
	import cake from "../../../../core/assets/icons/cake.svg?raw";
	import Button from "../Button.svelte";
	import { createEventDispatcher } from "svelte";
	import type { ProductLabel } from "../../../../core/schema/ProductLabel.js";
	import Drawer from "../Drawer/Drawer.svelte";
	import Modal from "../Modal.svelte";
	import standingPrintOnCake from "../../assets/images/stojici-tisk-dort.svg";
	import lyingPrintOnCake from "../../assets/images/lezici-tisk-dort.svg";
	import meringuesOnCake from "../../assets/images/pusinky-dort.svg";
	import labelsOnCake from "../../assets/images/cedulky-dort.svg";
	import numbersOnCake from "../../assets/images/cislo-dort.svg";
	import creamOnCake from "../../assets/images/slehacka-dort.svg";
	import { ProductOption } from "../../../../core/schema/ProductOption.js";
	import type { ProductPhoto } from "../../../../core/schema/ProductPhoto.js";
	import bezlepek from "../../../../core/assets/icons/bezlepek.svg?raw";
	import bezlaktoza from "../../../../core/assets/icons/bezlaktoza.svg?raw";
	import ovocenavic from "../../../../core/assets/icons/ovocenavic.svg?raw";
	import pusinky from "../../../../core/assets/icons/pusinky.svg?raw";
	import { getPricesForOptions } from "./getPricesForOptions.js";
	import { minimalOptions } from "../../../../core/schema/productOptionPrices.js";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import { getPriceOfOrderProduct } from "../../../../core/schema/utils/getPriceOfOrderProduct.js";
	import { transformProductToOrderProduct } from "../../../../core/schema/utils/transformProductToOrderProduct.js";
	import { doesProductForCustomerHaveMultipleSizes } from "../../../../core/schema/utils/doesProductForCustomerHaveMultipleSizes.js";
	import StaticPicture from "../Picture/StaticPicture.svelte";
	import logoImage from "../../../../core/assets/logo.svg";
	import { appName } from "../../../../core/schema/appName.js";
	import type { CreamColor } from "../../../../core/schema/CreamColor.js";
	import Form from "../Form/Form.svelte";
	import { hasOptions } from "../../../../core/schema/utils/hasOptions.js";
	import type { ProductMacaroons } from "../../../../core/schema/ProductMacaroons.js";
	import { maxMacaroonsPerSize } from "../../../../core/schema/productOptionsConstants.js";
	import { inflectMacaroons } from "./inflectMacaroons.js";
	import type { ProductNumber } from "../../../../core/schema/ProductNumber.js";
	import ProductConfiguratorNumbers from "./ProductConfiguratorNumbers.svelte";
	import ProductConfiguratorLabels from "./ProductConfiguratorLabels.svelte";
	import ProductConfiguratorCreamColor from "./ProductConfiguratorCreamColor.svelte";
	import ProductConfiguratorMacaroons from "./ProductConfiguratorMacaroons.svelte";
	import ProductConfiguratorPhotos from "./ProductConfiguratorPhotos.svelte";
	import ProductOptionDrawer from "./ProductOptionDrawer.svelte";
	import cislodort from "../../../../core/assets/icons/cislodort.svg?raw";
	import cedulkydort from "../../../../core/assets/icons/cedulkydort.svg?raw";
	import photooncake from "../../../../core/assets/icons/photooncake.svg?raw";
	import cakeConf from "../../../../core/assets/icons/cakeconf.svg?raw";
	import macaroonIcon from "../../../../core/assets/icons/makronky.svg?raw";
	import type { OpenableOption } from "./OpenableOption.js";

	export let isOpen: boolean;
	export let product: ProductForCustomer;
	export let options: OrderProductOptions;

	const { TENANT_LOCALE } = getGlobalSettings();
	const dispatch = createEventDispatcher<{
		saveOptions: OrderProductOptions;
	}>();

	let currentOptions = options;
	let lyingPhoto: ProductPhoto | undefined = undefined;
	let creamColor: CreamColor | undefined = undefined;
	let macaroons: ProductMacaroons | undefined = undefined;
	let labels: (ProductLabel & { id: string })[] = [];
	let numbers: ProductNumber[] = [];
	let standingPhotos: { id: string; photo?: ProductPhoto }[] = [];

	$: labelsEnabled = labels.length > 0;
	$: numbersEnabled = numbers.length > 0;
	$: creamColorEnabled = creamColor !== undefined;
	$: macaroonsEnabled = macaroons !== undefined;
	$: photosEnabled = lyingPhoto !== undefined || standingPhotos.length > 0;

	function initializeOptions(options: OrderProductOptions): void {
		({ lyingPhoto, creamColor, labels, macaroons, numbers } = options);
		standingPhotos = options.standingPhotos.map((photo) => ({ id: crypto.randomUUID(), photo }));

		labelsEnabled = labels.length > 0;
		numbersEnabled = numbers.length > 0;
		creamColorEnabled = creamColor !== undefined;
		macaroonsEnabled = macaroons !== undefined;
		photosEnabled = lyingPhoto !== undefined || standingPhotos.length > 0;
	}

	initializeOptions(currentOptions);

	let form: HTMLFormElement | null = null;
	function updateCurrentOptions(form: HTMLFormElement): OrderProductOptions {
		const formData = new FormData(form);

		const mappedStandingPhotos = standingPhotos
			.map(({ photo }) => photo)
			.filter((photo): photo is ProductPhoto => photo !== undefined);

		currentOptions = {
			...options,
			lyingPhoto,
			standingPhotos: mappedStandingPhotos,
			labels,
			extraFruit: Boolean(formData.get("extraFruitEnabled")),
			glutenFree: Boolean(formData.get("glutenFreeEnabled")),
			lactoseFree: Boolean(formData.get("lactoseFreeEnabled")),
			meringues: Boolean(formData.get("meringuesEnabled")),
			numbers,
			macaroons: macaroonsEnabled ? macaroons : undefined,
			creamColor: creamColorEnabled ? creamColor : undefined,
		};
		return currentOptions;
	}

	let isDirty = false;
	function onFormChange(): void {
		if (!form) {
			return;
		}
		isDirty = true;

		updateCurrentOptions(form);
	}

	let openedOption: OpenableOption | undefined;

	function removeNumbersChanges(): void {
		numbers = [];
		openedOption = undefined;
	}
	function removeLabelsChanges(): void {
		labels = [];
		openedOption = undefined;
	}
	function removeCreamColorChanges(): void {
		creamColor = undefined;
		openedOption = undefined;
	}
	function removeMacaroonsChanges(): void {
		macaroons = undefined;
		openedOption = undefined;
	}
	function removePhotosChanges(): void {
		lyingPhoto = undefined;
		standingPhotos = [];
		openedOption = undefined;
	}

	let showCloseModal = false;
	function onCancel(): void {
		isOpen = false;
		openedOption = undefined;
	}

	function onContinue(): void {
		showCloseModal = false;
	}

	function onClose(): boolean {
		if (!isDirty) {
			openedOption = undefined;
			return true;
		}

		showCloseModal = true;
		return false;
	}

	function onOpenChanged(open: boolean): void {
		if (open) {
			currentOptions = options;
			initializeOptions(currentOptions);
		} else {
			showCloseModal = false;
			isDirty = false;
		}
	}

	$: onOpenChanged(isOpen);

	$: basePrice = getPriceForProductForCustomerOfSize(product, options.size)?.withTax;
	$: minimalPricesForOptions = getPricesForOptions(basePrice, minimalOptions);
	$: pricesForOptions = getPricesForOptions(basePrice, currentOptions);
	$: totalPrice = getPriceOfOrderProduct(
		transformProductToOrderProduct(crypto.randomUUID(), product, 1, currentOptions),
	);

	function submit(): void {
		if (!form?.reportValidity()) {
			return;
		}

		const newOptions = updateCurrentOptions(form);
		dispatch("saveOptions", newOptions);
	}

	function updateNumbers(newNumbers: ProductNumber[]): void {
		if (!form?.reportValidity()) {
			return;
		}
		numbers = newNumbers;
		openedOption = undefined;
		onFormChange();
	}
	function updateLabels(newLabels: ProductLabel[]): void {
		if (!form?.reportValidity()) {
			return;
		}
		labels = newLabels;
		openedOption = undefined;
		onFormChange();
	}
	function updateCreamColor(newCreamColor: CreamColor | undefined): void {
		if (!form?.reportValidity()) {
			return;
		}
		creamColor = newCreamColor;
		openedOption = undefined;
		onFormChange();
	}
	function updateMacaroons(newMacaroons: ProductMacaroons | undefined): void {
		if (!form?.reportValidity()) {
			return;
		}
		macaroons = newMacaroons;
		openedOption = undefined;
		onFormChange();
	}
	function updatePhotos(
		newLyingPhoto: ProductPhoto | undefined,
		newStandingPhotos: { id: string; photo?: ProductPhoto }[],
	): void {
		if (!form?.reportValidity()) {
			return;
		}
		lyingPhoto = newLyingPhoto;
		standingPhotos = newStandingPhotos;
		openedOption = undefined;
		onFormChange();
	}
</script>

<Modal bind:show={showCloseModal} class="max-w-[27.5rem] !p-4 sm:!p-8">
	<div class="text-center">
		<span>Zavřením panelu zrušíte všechny úpravy na dortu a budete pak muset začít znovu.</span>
	</div>

	<div slot="actions" class="flex flex-wrap items-center justify-between gap-2 sm:gap-0">
		<Button class="border-0" variant="secondary" on:click={onCancel}>Zrušit úpravy</Button>
		<Button variant="secondary" on:click={onContinue}>Pokračovat v úpravách</Button>
	</div>
</Modal>

<Drawer
	noPadding
	bind:isOpen
	class="w-full sm:w-[32rem] {openedOption !== undefined &&
		'mr-[12.625rem] delay-[250ms]'} transition-all duration-200 ease-out"
	position="right"
	{onClose}
>
	{@const [primaryImage] = product.images}

	<Form
		bind:form
		class="flex h-full w-full flex-col pb-[11.5rem] pt-7"
		on:change={onFormChange}
		on:input={onFormChange}
	>
		<div class="flex w-full flex-col px-4 sm:px-8 md:px-14">
			<p class="text-md mb-6 font-semibold sm:text-lg">Upravit</p>
			<div class="flex w-full justify-between">
				<div
					class="grid w-full grid-cols-1 grid-rows-1 gap-x-3 gap-y-1 sm:grid-cols-[5.5rem,auto] sm:gap-x-6 sm:gap-y-2"
				>
					<div class="flex gap-2">
						{#if primaryImage}
							<DynamicPicture
								image={primaryImage}
								width={80}
								height={80}
								bucket="storage"
								class="row-span-3"
								imgClass="rounded-lg h-[88px] w-[88px] object-contain"
								alt={product.name[TENANT_LOCALE]}
								loading="eager"
							/>
						{:else}
							<div class="bg-secondary-40 row-span-3 flex h-24 items-center justify-center rounded-lg sm:h-[7.5rem]">
								<StaticPicture
									alt="Logo {appName}"
									image={logoImage}
									class="h-16 w-16 sm:h-20 sm:w-20"
									width={80}
									height={80}
									loading="eager"
								/>
							</div>
						{/if}
						<p class="text-base font-semibold sm:hidden">
							{product.name[TENANT_LOCALE]}
						</p>
					</div>
					<div class="flex flex-col justify-between">
						<p class="text-md hidden font-semibold sm:inline">
							{product.name[TENANT_LOCALE]}
						</p>
						<div class="text-2xs mt-2 flex w-full items-center justify-between sm:mt-0 sm:text-xs">
							<div class="flex items-center gap-3" class:hidden={!doesProductForCustomerHaveMultipleSizes(product)}>
								Velikost
								<span
									class="border-secondary text-secondary text-3xs sm:text-2xs flex h-6 w-6 cursor-default select-none items-center justify-center truncate rounded-full border bg-white py-2 font-semibold uppercase leading-3 transition-all duration-300 sm:h-7 sm:w-7"
								>
									{options.size}
								</span>
							</div>
							<p>
								Základní cena: <span class="font-semibold">{formatPrice(basePrice ?? 0, TENANT_LOCALE, false)}</span>
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="mt-6 flex flex-1 flex-col gap-2 overflow-y-auto px-4 sm:px-8 md:px-14">
			{#if product.enabledOptions.includes(ProductOption.GlutenFreeVariant)}
				<ProductConfiguratorOption
					id="glutenFreeEnabled"
					name="Bezlepková varianta"
					{minimalPricesForOptions}
					minimalPriceIsFinal
					{pricesForOptions}
					enabled={currentOptions.glutenFree}
					icon={bezlepek}
					tooltipCharacter="!"
					tooltipPosition="bottom"
					productOption={ProductOption.GlutenFreeVariant}
				>
					<div slot="tooltip">
						Počítejte, prosím, s možnými stopami lepku, protože vše vyrábíme pod jednou střechou.
					</div>
				</ProductConfiguratorOption>
			{/if}

			{#if product.enabledOptions.includes(ProductOption.LactoseFreeVariant)}
				<ProductConfiguratorOption
					id="lactoseFreeEnabled"
					name="Bezlaktózová varianta"
					{minimalPricesForOptions}
					minimalPriceIsFinal
					{pricesForOptions}
					enabled={currentOptions.lactoseFree}
					icon={bezlaktoza}
					tooltipCharacter="!"
					tooltipPosition="bottom"
					productOption={ProductOption.LactoseFreeVariant}
				>
					<div slot="tooltip">
						Počítejte, prosím, s možnými stopami laktózy, protože vše vyrábíme pod jednou střechou.
					</div>
				</ProductConfiguratorOption>
			{/if}

			{#if product.enabledOptions.includes(ProductOption.ExtraFruit)}
				<ProductConfiguratorOption
					id="extraFruitEnabled"
					name="Ovoce navíc"
					{minimalPricesForOptions}
					minimalPriceIsFinal
					{pricesForOptions}
					enabled={currentOptions.extraFruit}
					icon={ovocenavic}
					tooltipPosition="bottom"
					productOption={ProductOption.ExtraFruit}
				>
					<div slot="tooltip">Dort vám bohatě přizdobíme krásnými jahodami, malinami a borůvkami.</div>
				</ProductConfiguratorOption>
			{/if}

			{#if product.enabledOptions.includes(ProductOption.Numbers)}
				<ProductConfiguratorOption
					id="numbersEnabled"
					name="Čokoládové číslice"
					{minimalPricesForOptions}
					{pricesForOptions}
					icon={cislodort}
					tooltipImages={[{ src: numbersOnCake, alt: "Vizualizace čísla na dortu" }]}
					tooltipPosition="bottom"
					bind:openedOption
					productOption={ProductOption.Numbers}
					cancelChanges={removeNumbersChanges}
					enabled={numbersEnabled}
				>
					<div slot="tooltip">
						Používáme čokoládové číslice z bílé nebo hořké čokolády, které si sami vyrábíme. Naše cukrářky zvolí daný
						druh číslic s ohledem na to, aby s vaším dortem ladily.
					</div>
					<ProductOptionDrawer
						slot="drawer"
						bind:openedOption
						productOption={ProductOption.Numbers}
						removeChanges={removeNumbersChanges}
					>
						<ProductConfiguratorNumbers {numbers} {updateNumbers} {minimalPricesForOptions} {basePrice} />
					</ProductOptionDrawer>
				</ProductConfiguratorOption>
			{/if}

			{#if product.enabledOptions.includes(ProductOption.Labels)}
				<ProductConfiguratorOption
					id="labelEnabled"
					name="Přidat cedulku"
					{minimalPricesForOptions}
					{pricesForOptions}
					icon={cedulkydort}
					tooltipImages={[{ src: labelsOnCake, alt: "Vizualizace cedulek na dortu" }]}
					bind:openedOption
					productOption={ProductOption.Labels}
					cancelChanges={removeLabelsChanges}
					enabled={labelsEnabled}
				>
					<div slot="tooltip">
						Ručně psané jméno nebo krátký vzkaz na našich čokoládových destičkách z dortu udělá zároveň i krásné gesto.
					</div>

					<ProductOptionDrawer
						slot="drawer"
						bind:openedOption
						productOption={ProductOption.Labels}
						removeChanges={removeLabelsChanges}
					>
						<ProductConfiguratorLabels {minimalPricesForOptions} {labels} {updateLabels} {basePrice} />
					</ProductOptionDrawer>
				</ProductConfiguratorOption>
			{/if}

			{#if product.enabledOptions.includes(ProductOption.LyingPhoto) || product.enabledOptions.includes(ProductOption.StandingPhotos)}
				<ProductConfiguratorOption
					id="photosEnabled"
					name="Jedlý tisk"
					icon={photooncake}
					{minimalPricesForOptions}
					{pricesForOptions}
					tooltipImages={[
						{ src: lyingPrintOnCake, alt: "Vizualizace ležícího tisku na dortu" },
						{ src: standingPrintOnCake, alt: "Vizualizace stojícího tisku na dortu" },
					]}
					bind:openedOption
					productOption="photos"
					cancelChanges={removePhotosChanges}
					enabled={photosEnabled}
				>
					<div slot="tooltip">
						Nahrajte obrázky, které si přejete vytisknout. Pro nejlepší efekt volte vysokou kvalitu. Jedlý papír je
						bezlepkový.
					</div>

					<ProductOptionDrawer
						slot="drawer"
						bind:openedOption
						productOption="photos"
						removeChanges={removePhotosChanges}
					>
						<ProductConfiguratorPhotos
							{minimalPricesForOptions}
							{basePrice}
							allowLying={product.enabledOptions.includes(ProductOption.LyingPhoto)}
							allowStanding={product.enabledOptions.includes(ProductOption.StandingPhotos)}
							{lyingPhoto}
							{standingPhotos}
							{updatePhotos}
						/>
					</ProductOptionDrawer>
				</ProductConfiguratorOption>
			{/if}

			{#if product.enabledOptions.includes(ProductOption.Meringues)}
				<ProductConfiguratorOption
					id="meringuesEnabled"
					name="Přidat sněhové pusinky"
					{minimalPricesForOptions}
					minimalPriceIsFinal
					{pricesForOptions}
					enabled={currentOptions.meringues}
					icon={pusinky}
					tooltipImages={[{ src: meringuesOnCake, alt: "Vizualizace pusinek na dortu" }]}
					productOption={ProductOption.Meringues}
				>
					<div slot="tooltip">Několik barevných pusinek a dort je hned veselejší!</div>
				</ProductConfiguratorOption>
			{/if}

			{#if product.enabledOptions.includes(ProductOption.CreamColor)}
				<ProductConfiguratorOption
					id="creamColorEnabled"
					name="Pokrýt barevnou šlehačkou"
					{minimalPricesForOptions}
					{pricesForOptions}
					icon={cakeConf}
					tooltipImages={[{ src: creamOnCake, alt: "Vizualizace dortu pokrytého šlehačkou" }]}
					bind:openedOption
					productOption={ProductOption.CreamColor}
					cancelChanges={removeCreamColorChanges}
					enabled={creamColorEnabled}
				>
					<div slot="tooltip">
						Dort obmažeme nahoře i po bocích vámi vybranou barvou. Ideální volba pro kombinaci s jedlým tiskem.
					</div>

					<ProductOptionDrawer
						slot="drawer"
						bind:openedOption
						productOption={ProductOption.CreamColor}
						removeChanges={removeCreamColorChanges}
					>
						<ProductConfiguratorCreamColor {minimalPricesForOptions} {basePrice} {creamColor} {updateCreamColor} />
					</ProductOptionDrawer>
				</ProductConfiguratorOption>
			{/if}

			{#if product.enabledOptions.includes(ProductOption.Macaroons)}
				{@const max = maxMacaroonsPerSize[currentOptions.size]}
				<ProductConfiguratorOption
					id="macaroonsEnabled"
					name="Makronky"
					{minimalPricesForOptions}
					{pricesForOptions}
					icon={macaroonIcon}
					bind:openedOption
					productOption={ProductOption.Macaroons}
					cancelChanges={removeMacaroonsChanges}
					enabled={macaroonsEnabled}
				>
					<div slot="tooltip">
						Přidej si na svůj dort až {max}
						{inflectMacaroons(max)} podle své chuti.
					</div>

					<ProductOptionDrawer
						slot="drawer"
						bind:openedOption
						productOption={ProductOption.Macaroons}
						removeChanges={removeMacaroonsChanges}
					>
						<ProductConfiguratorMacaroons
							{minimalPricesForOptions}
							{basePrice}
							{macaroons}
							{updateMacaroons}
							size={currentOptions.size}
						/>
					</ProductOptionDrawer>
				</ProductConfiguratorOption>
			{/if}
		</div>

		<div
			class="absolute bottom-0 left-0 h-[10.625rem] w-full rounded-t-[22px] bg-white p-6 shadow-[0_-6px_8px_-5px_rgba(225,225,225,225)]"
		>
			<div class="text-md flex items-end justify-between font-semibold sm:text-lg">
				<span class="text-md">Celková cena</span>
				<span class="text-lg">{nbspify(formatPrice(totalPrice.withTax, TENANT_LOCALE, false))}</span>
			</div>

			<Button
				class="mt-4 w-full justify-center !rounded-[1.875rem] py-[.875rem] text-[1.125rem] leading-6 sm:py-[1.063rem] md:text-[1.375rem]"
				icon={cake}
				iconClass="w-6"
				variant="primary"
				on:click={submit}
			>
				{#if !hasOptions(options)}
					Přidat úpravy
				{:else}
					Změnit úpravy
				{/if}
			</Button>
		</div>
	</Form>
</Drawer>
